import React, { useEffect, useMemo, useState } from "react";
import api, { API } from "@/lib/api";
import { PageHeader, Card, Button, Input, Select, Field, Stat, Textarea } from "@/components/UI";
import { Plus, PencilSimple, Trash, ForkKnife, ArrowCounterClockwise, ChefHat, Book, FileXls, Printer } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { today, fmt, rid, EMPTY_RATION, EMPTY_CAT } from "./constants";

export function RationsTab({ rations, stocks, catalog, feedOrder, reload }) {
  const { user } = useAuth();
  const canSeeCosts = !!user?.can_see_costs;
  const [editing, setEditing] = useState(null); // null | ration (edit mode)
  const [showForm, setShowForm] = useState(false);
  const [historyFor, setHistoryFor] = useState(null); // { ...ration } — versiyon modalı

  const startNew = () => { setEditing({ ...EMPTY_RATION }); setShowForm(true); };
  const startEdit = (r) => { setEditing({ ...r }); setShowForm(true); };

  const feedNow = async (r) => {
    const eff = r.effective_animal_count ?? r.animal_count ?? 0;
    if (eff <= 0) {
      toast.error(`${r.name}: önce "Yemlenecek Hayvan Sayısı" veya "Kayıtlı Hayvan Sayısı" girin`);
      return;
    }
    const label = r.feeding_animal_count && r.feeding_animal_count !== r.animal_count
      ? `${eff} yemlenecek (padokta ${r.animal_count} kayıtlı)`
      : `${eff} baş`;
    if (!window.confirm(`${r.name} rasyonunun bugünkü yemlemesi kaydedilsin mi?\n\n${label} × günlük tüketim stoktan düşülecek.`)) return;
    try {
      const res = await api.post(`/rations/${r.id}/feed`, { ration_id: r.id, date: today() });
      const w = res.data?.warnings || [];
      const costPart = canSeeCosts ? ` (${fmt(res.data.total_cost, 0)} ₺)` : "";
      toast.success(`Yemleme kaydedildi${costPart}${w.length ? " — " + w.length + " uyarı" : ""}`);
      w.forEach(msg => toast.warning(msg, { duration: 6000 }));
      reload();
    } catch (err) { toast.error(err?.message || "Hata"); }
  };

  const del = async (r) => {
    if (!window.confirm(`"${r.name}" rasyonu silinsin mi? Geçmiş yemleme kayıtları korunur.`)) return;
    try { await api.delete(`/rations/${r.id}`); toast.success("Silindi"); reload(); }
    catch (err) { toast.error(err?.message || "Hata"); }
  };

  if (showForm && editing) {
    return <RationEditor initial={editing} stocks={stocks} catalog={catalog} feedOrder={feedOrder}
                         onSave={() => { setShowForm(false); setEditing(null); reload(); }}
                         onCancel={() => { setShowForm(false); setEditing(null); }} />;
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="heading text-lg">Rasyonlar</h3>
        <Button data-testid="rations-new-btn" onClick={startNew}>
          <Plus size={14} className="inline mr-1" /> Yeni Rasyon
        </Button>
      </div>

      {rations.length === 0 && (
        <div className="text-center py-12 text-[#8a938c]">
          <ChefHat size={40} className="mx-auto mb-2 opacity-50" />
          Henüz rasyon yok. "Yeni Rasyon" ile başlayın: Süt Sağan İnek, Kuruda İnek, Gebe Düve, Genç Düve, Buzağı, Boğa vb.
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {rations.map(r => (
          <div key={r.id} data-testid={`rations-card-${r.id}`}
               className="border border-[#E5E2DC] rounded-md p-4 bg-white hover:shadow-sm transition">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-bold text-base">{r.name}</div>
                <div className="text-xs text-[#8a938c]">
                  {r.feeding_animal_count && r.feeding_animal_count !== r.animal_count ? (
                    <><b className="text-[#1A1D1A]">{r.feeding_animal_count}</b> yemlenecek / {r.animal_count} kayıtlı</>
                  ) : (
                    <><b className="text-[#1A1D1A]">{r.animal_count}</b> baş</>
                  )}
                  {" • "}{r.items?.length || 0} yem kalemi
                </div>
                {(r.meals_per_day > 1 || (r.meal_times && r.meal_times.length > 0)) && (
                  <div className="text-[11px] text-[#5e675e] mt-0.5">
                    <b>{r.meals_per_day || 2} öğün</b>
                    {r.meal_times && r.meal_times.length > 0 && <> · {r.meal_times.join(" · ")}</>}
                  </div>
                )}
              </div>
              <div className="flex gap-1">
                <button data-testid={`rations-edit-${r.id}`} onClick={() => startEdit(r)}
                        className="p-1.5 hover:bg-[#f2ede4] rounded" title="Düzenle"><PencilSimple size={16} /></button>
                <button data-testid={`rations-del-${r.id}`} onClick={() => del(r)}
                        className="p-1.5 hover:bg-[#fbeee9] text-[#C45B45] rounded" title="Sil"><Trash size={16} /></button>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-2 text-xs my-3 py-3 border-y border-[#E5E2DC]">
              <div><div className="text-[#8a938c]">Günlük/baş</div><div className="font-semibold tabnum">{fmt(r.per_head_kg, 2)} kg</div></div>
              <div><div className="text-[#8a938c]">Günlük toplam</div><div className="font-semibold tabnum">{fmt(r.total_kg, 1)} kg</div></div>
              <div><div className="text-[#8a938c]">Protein/baş</div><div className="font-semibold tabnum">{fmt(r.per_head_protein_g, 0)} g</div></div>
              <div><div className="text-[#8a938c]">Enerji/baş</div><div className="font-semibold tabnum">{fmt(r.per_head_energy_mcal, 1)} Mcal</div></div>
            </div>

            {/* Öğün başına hazırlanacak miktar */}
            <div className="mb-3 p-2.5 bg-[#EEF3E8] border border-[#C9D6BE] rounded" data-testid={`rations-per-meal-${r.id}`}>
              <div className="flex items-center justify-between">
                <div className="text-xs text-[#3E5B36] font-medium">
                  Öğün Başına Hazırlanacak ({r.meals_per_day || 2} öğün)
                </div>
                <div className="text-lg font-bold text-[#3E5B36] tabnum">
                  {fmt(r.per_meal_kg, 1)} kg
                </div>
              </div>
              <div className="text-[11px] text-[#5e675e] mt-0.5">
                Baş başına {fmt(r.per_meal_per_head_kg, 2)} kg · günlük {fmt(r.total_kg, 1)} kg / {r.meals_per_day || 2} öğün
              </div>
            </div>

            {/* Grup dağıtımı — hangi padoklara ne kadar yem (orana göre) */}
            {Array.isArray(r.distribution) && r.distribution.length > 0 && (
              <div className="mb-3 p-2.5 bg-[#FAF7F1] border border-[#E5E2DC] rounded" data-testid={`rations-distribution-${r.id}`}>
                <div className="text-xs font-semibold text-[#1A1D1A] mb-1.5">Grup Bazlı Dağıtım (Orana Göre)</div>
                <div className="space-y-1">
                  {r.distribution.map((d, di) => (
                    <div key={`${d.name}-${di}`} className="flex items-center justify-between text-[11px]" data-testid={`rations-distribution-row-${r.id}-${di}`}>
                      <div className="truncate flex-1">
                        <b className="text-[#1A1D1A]">{d.name}</b>
                        <span className="text-[#8a938c]"> · {d.animal_count} · %{fmt(d.ratio_pct, 1)}</span>
                      </div>
                      <div className="tabnum text-right">
                        <span className="text-[#5e675e]">{fmt(d.daily_kg, 1)} kg/gün</span>
                        <span className="text-[#3E5B36] font-semibold"> · {fmt(d.per_meal_kg, 1)} kg/öğün</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {canSeeCosts && (
              <div className="flex justify-between items-baseline mb-3">
                <span className="text-xs text-[#8a938c]">Günlük Maliyet</span>
                <span className="text-xl font-bold text-[#C45B45] tabnum">{fmt(r.total_cost, 0)} ₺</span>
              </div>
            )}

            {/* Maliyet Değişim Analizi (bir önceki versiyona kıyasla) */}
            {canSeeCosts && r.cost_change && (
              <div
                className={`mb-3 p-2.5 rounded border ${
                  r.cost_change.direction === "up"
                    ? "bg-[#FBECEA] border-[#E9B5AB]"
                    : r.cost_change.direction === "down"
                      ? "bg-[#E4EEE6] border-[#c2d6c8]"
                      : "bg-[#F5F2EC] border-[#E5E2DC]"
                }`}
                data-testid={`rations-cost-change-${r.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-[11px] font-semibold uppercase tracking-wider">
                    <span className={
                      r.cost_change.direction === "up" ? "text-[#8B2E1D]"
                      : r.cost_change.direction === "down" ? "text-[#2D4A36]" : "text-[#5e675e]"
                    }>
                      Maliyet Değişimi
                    </span>
                  </div>
                  <div className={`text-sm font-bold tabnum ${
                    r.cost_change.direction === "up" ? "text-[#C45B45]"
                    : r.cost_change.direction === "down" ? "text-[#3E5B36]" : "text-[#5e675e]"
                  }`}>
                    {r.cost_change.direction === "up" ? "▲" : r.cost_change.direction === "down" ? "▼" : "="}{" "}
                    {r.cost_change.pct_total !== null ? `%${fmt(Math.abs(r.cost_change.pct_total), 1)}` : "—"}
                    <span className="ml-1 text-xs font-normal">
                      ({r.cost_change.delta_total > 0 ? "+" : ""}{fmt(r.cost_change.delta_total, 0)} ₺)
                    </span>
                  </div>
                </div>
                <div className="text-[10px] text-[#5e675e] mt-0.5 tabnum flex items-center justify-between">
                  <span>Önceki: {fmt(r.cost_change.prev_total_cost, 0)} ₺</span>
                  <button
                    data-testid={`rations-history-btn-${r.id}`}
                    onClick={() => setHistoryFor(r)}
                    className="underline hover:text-[#1A1D1A] text-[10px]"
                  >
                    Geçmiş ({r.version_count} versiyon)
                  </button>
                </div>
              </div>
            )}
            {canSeeCosts && !r.cost_change && r.version_count === 0 && (
              <div className="mb-3 text-[11px] text-[#8a938c] italic text-center">
                İlk versiyon — güncellendikçe maliyet değişimi burada görünür
              </div>
            )}

            {r.fed_today ? (
              <div className="w-full text-center bg-[#E4EEE6] border border-[#c2d6c8] text-[#2D4A36] py-2 rounded text-sm font-semibold" data-testid={`rations-fed-${r.id}`}>
                ✓ Bugün yemlendi
              </div>
            ) : (
              <Button data-testid={`rations-feed-${r.id}`} onClick={() => feedNow(r)} className="w-full">
                <ForkKnife size={14} className="inline mr-1" /> Bugün Yemle
              </Button>
            )}
          </div>
        ))}
      </div>
      {historyFor && <RationVersionModal ration={historyFor} onClose={() => setHistoryFor(null)} />}
    </Card>
  );
}

// ---------------- RATION VERSION HISTORY MODAL ----------------

function RationVersionModal({ ration, onClose }) {
  const [versions, setVersions] = useState(null);
  useEffect(() => {
    api.get(`/rations/${ration.id}/versions`)
      .then(r => setVersions(r.data))
      .catch(() => setVersions([]));
  }, [ration.id]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose} data-testid="ration-version-modal">
      <Card className="max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="heading text-lg">{ration.name} — Versiyon Geçmişi</h3>
            <div className="text-xs text-[#5e675e]">
              Şu anki: {fmt(ration.total_cost, 0)} ₺/gün · {fmt(ration.per_head_cost, 2)} ₺/baş
            </div>
          </div>
          <Button onClick={onClose} className="!bg-white !text-[#1A1D1A] border border-[#E5E2DC]">Kapat</Button>
        </div>

        {versions === null ? (
          <div className="text-center py-8 text-[#8a938c]">Yükleniyor...</div>
        ) : versions.length === 0 ? (
          <div className="text-center py-8 text-[#8a938c] italic">
            Bu rasyonda henüz güncelleme yapılmamış. Rasyonu düzenleyip kaydettiğinizde eski hali burada arşivlenir.
          </div>
        ) : (
          <div className="space-y-3">
            {/* Karşılaştırma başlığı (current vs latest) */}
            {ration.cost_change && (
              <div className="p-3 rounded border bg-[#F5F2EC] border-[#E5E2DC]">
                <div className="text-xs font-semibold text-[#1A1D1A] mb-1">Şu Anki ↔ Son Versiyon</div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] text-[#8a938c] uppercase">Toplam Maliyet</div>
                    <div className="tabnum">
                      <span className="text-[#8a938c]">{fmt(ration.cost_change.prev_total_cost, 0)} ₺</span>
                      {" → "}
                      <b className="text-[#1A1D1A]">{fmt(ration.total_cost, 0)} ₺</b>
                    </div>
                    <div className={`text-xs mt-0.5 tabnum font-semibold ${
                      ration.cost_change.direction === "up" ? "text-[#C45B45]"
                      : ration.cost_change.direction === "down" ? "text-[#3E5B36]" : "text-[#5e675e]"
                    }`}>
                      {ration.cost_change.direction === "up" ? "▲ " : ration.cost_change.direction === "down" ? "▼ " : "= "}
                      {ration.cost_change.pct_total !== null ? `%${fmt(Math.abs(ration.cost_change.pct_total), 1)}` : "—"}
                      {" ("}
                      {ration.cost_change.delta_total > 0 ? "+" : ""}{fmt(ration.cost_change.delta_total, 0)} ₺
                      {")"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] text-[#8a938c] uppercase">Baş Başına</div>
                    <div className="tabnum">
                      <span className="text-[#8a938c]">{fmt(ration.cost_change.prev_per_head_cost, 2)} ₺</span>
                      {" → "}
                      <b className="text-[#1A1D1A]">{fmt(ration.per_head_cost, 2)} ₺</b>
                    </div>
                    <div className={`text-xs mt-0.5 tabnum font-semibold ${
                      ration.cost_change.direction === "up" ? "text-[#C45B45]"
                      : ration.cost_change.direction === "down" ? "text-[#3E5B36]" : "text-[#5e675e]"
                    }`}>
                      {ration.cost_change.pct_per_head !== null ? `%${fmt(Math.abs(ration.cost_change.pct_per_head), 1)}` : "—"}
                      {" ("}
                      {ration.cost_change.delta_per_head > 0 ? "+" : ""}{fmt(ration.cost_change.delta_per_head, 2)} ₺
                      {")"}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Versiyon tablosu */}
            <div className="overflow-hidden border border-[#E5E2DC] rounded">
              <table className="data-table text-xs">
                <thead>
                  <tr>
                    <th>Tarih</th>
                    <th>Kim</th>
                    <th className="text-right">Toplam</th>
                    <th className="text-right">₺/baş</th>
                    <th className="text-right">Baş</th>
                    <th>Öğün</th>
                    <th>Yemler</th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map((v, vi) => (
                    <tr key={v.id} data-testid={`ration-version-row-${vi}`}>
                      <td className="tabnum whitespace-nowrap">{(v.snapshot_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="text-[10px]">{v.snapshot_by || "—"}</td>
                      <td className="text-right tabnum font-semibold">{v.total_cost != null ? `${fmt(v.total_cost, 0)} ₺` : "—"}</td>
                      <td className="text-right tabnum">{v.per_head_cost != null ? `${fmt(v.per_head_cost, 2)} ₺` : "—"}</td>
                      <td className="text-right tabnum">{v.effective_animal_count}</td>
                      <td className="tabnum">{v.meals_per_day || 2}</td>
                      <td className="text-[10px] max-w-xs truncate">
                        {(v.items || []).map(it => `${it.name} (${it.amount_per_head_kg}kg)`).join(", ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

// ---------------- RATION EDITOR (create/edit form) ----------------

function RationEditor({ initial, stocks, catalog, feedOrder, onSave, onCancel }) {
  const { user } = useAuth();
  const canSeeCosts = !!user?.can_see_costs;
  const [form, setForm] = useState(() => ({
    ...initial,
    items: (initial.items || []).map(it => ({ _uid: it._uid || rid(), ...it })),
    feeding_groups: (initial.feeding_groups || []).map(g => ({ _uid: g._uid || rid(), ...g })),
  }));
  const [saving, setSaving] = useState(false);

  // Yem listeleri 'Yapım Sırası'na (feed_order) göre gösterilir.
  const orderPos = useMemo(() => {
    const m = {};
    (feedOrder || []).forEach((f, i) => { if (!(f.name in m)) m[f.name] = i; });
    return m;
  }, [feedOrder]);
  const stockOptions = useMemo(() => {
    const pos = (n) => (n in orderPos ? orderPos[n] : Infinity);
    return stocks
      .filter(s => (s.category || "").toLowerCase() !== "buzağı maması")
      .sort((a, b) => pos(a.name) - pos(b.name) || (a.name || "").localeCompare(b.name || "", "tr"));
  }, [stocks, orderPos]);
  const catalogOptions = useMemo(() => {
    const pos = (n) => (n in orderPos ? orderPos[n] : Infinity);
    return [...catalog].sort((a, b) => pos(a.name) - pos(b.name) || (a.name || "").localeCompare(b.name || "", "tr"));
  }, [catalog, orderPos]);

  const addItemFromStock = (stock) => {
    // Try to enrich with nutritional info from catalog if same name exists
    const cat = catalog.find(c => c.name.toLowerCase() === stock.name.toLowerCase());
    const item = {
      _uid: rid(),
      source: "stock",
      ref_id: stock.id,
      name: stock.name,
      amount_per_head_kg: 0,
      unit_price: Number(stock.unit_price || 0),
      protein_pct: cat ? cat.protein_pct : 0,
      energy_mcal: cat ? cat.energy_mcal : 0,
    };
    setForm(f => ({ ...f, items: [...(f.items || []), item] }));
  };

  const addItemFromCatalog = (cat) => {
    const item = {
      _uid: rid(),
      source: "catalog",
      ref_id: cat.id,
      name: cat.name,
      amount_per_head_kg: 0,
      unit_price: 0,
      protein_pct: cat.protein_pct || 0,
      energy_mcal: cat.energy_mcal || 0,
    };
    setForm(f => ({ ...f, items: [...(f.items || []), item] }));
  };

  const updateItem = (i, patch) => {
    setForm(f => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) }));
  };
  const removeItem = (i) => {
    setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  };

  const totalKgPerHead = form.items.reduce((s, it) => s + Number(it.amount_per_head_kg || 0), 0);
  // KURUMSAL FORMÜL: Üstteki 'Toplam Porsiyon Sayısı' mikserin toplam hazırlığını belirler.
  // Grup listesindeki sayılar YALNIZCA oran (ratio) için kullanılır.
  const effectiveCount = form.feeding_animal_count !== "" && form.feeding_animal_count != null
    ? Number(form.feeding_animal_count) : Number(form.animal_count || 0);
  const totalKgAll = totalKgPerHead * effectiveCount;
  const mealsCount = Math.max(Number(form.meals_per_day || 2), 1);
  const perMealKg = totalKgAll / mealsCount;
  const perMealPerHeadKg = totalKgPerHead / mealsCount;
  const totalCostAll = form.items.reduce((s, it) => s + Number(it.amount_per_head_kg || 0) * Number(it.unit_price || 0), 0) * effectiveCount;
  const perMealCost = totalCostAll / mealsCount;
  // Grup dağıtımı — ORANA göre canlı hesap
  const groupsSum = (form.feeding_groups || []).reduce((s, g) => s + Number(g.animal_count || 0), 0);
  const distributionLive = (form.feeding_groups || []).map(g => {
    const cnt = Number(g.animal_count || 0);
    const ratio = groupsSum > 0 ? cnt / groupsSum : 0;
    const daily = totalKgAll * ratio;
    return {
      name: g.name || "(isimsiz grup)",
      animal_count: cnt,
      ratio_pct: ratio * 100,
      daily_kg: daily,
      per_meal_kg: daily / mealsCount,
    };
  });
  const totalProteinG = form.items.reduce((s, it) => s + Number(it.amount_per_head_kg || 0) * Number(it.protein_pct || 0) * 10, 0);
  const totalEnergy = form.items.reduce((s, it) => s + Number(it.amount_per_head_kg || 0) * Number(it.energy_mcal || 0), 0);

  const save = async () => {
    // Parse meal_times: accept comma-separated "05:00, 12:00, 19:00" string OR array
    let mealTimes = form.meal_times;
    if (typeof mealTimes === "string") {
      mealTimes = mealTimes.split(",").map(s => s.trim()).filter(Boolean);
    }
    const body = {
      name: form.name.trim(),
      animal_count: Number(form.animal_count || 0),
      feeding_animal_count: form.feeding_animal_count !== "" && form.feeding_animal_count != null
        ? Number(form.feeding_animal_count) : null,
      feeding_groups: (form.feeding_groups || [])
        .map(g => ({ name: (g.name || "").trim(), animal_count: Number(g.animal_count || 0) }))
        .filter(g => g.name),
      meals_per_day: Number(form.meals_per_day || 2),
      meal_times: mealTimes,
      notes: form.notes || null,
      items: form.items.map(it => ({
        source: it.source, ref_id: it.ref_id, name: it.name,
        amount_per_head_kg: Number(it.amount_per_head_kg || 0),
        unit_price: Number(it.unit_price || 0),
        protein_pct: Number(it.protein_pct || 0),
        energy_mcal: Number(it.energy_mcal || 0),
      })).filter(it => it.amount_per_head_kg > 0),
    };
    if (!body.name) return toast.error("Rasyon adı boş olamaz");
    if (body.items.length === 0) return toast.error("En az bir yem kalemi ekleyin (miktarı 0'dan büyük)");
    setSaving(true);
    try {
      if (initial.id) {
        await api.patch(`/rations/${initial.id}`, body);
        toast.success("Rasyon güncellendi");
      } else {
        await api.post("/rations", body);
        toast.success("Rasyon oluşturuldu");
      }
      onSave();
    } catch (err) { toast.error(err?.message || "Hata"); }
    finally { setSaving(false); }
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="heading text-lg">{initial.id ? "Rasyon Düzenle" : "Yeni Rasyon"}</h3>
        <Button onClick={onCancel} className="!bg-white !text-[#1A1D1A] border border-[#E5E2DC]">Vazgeç</Button>
      </div>

      <div className="grid md:grid-cols-2 gap-3 mb-4">
        <Field label="Rasyon Adı *">
          <Input required data-testid="ration-form-name" value={form.name}
                 onChange={e => setForm({ ...form, name: e.target.value })}
                 placeholder="Örn: Süt Sağan İnek, Kuruda, Genç Düve" />
        </Field>
        <Field label="Padoktaki Kayıtlı Hayvan Sayısı (bilgi)">
          <Input type="number" min="0" data-testid="ration-form-count" value={form.animal_count}
                 onChange={e => setForm({ ...form, animal_count: e.target.value })}
                 placeholder="Örn: 50" />
        </Field>
        <Field label="Toplam Porsiyon Sayısı (mikser hazırlığı)">
          <Input type="number" min="0" data-testid="ration-form-feeding-count"
                 value={form.feeding_animal_count ?? ""}
                 onChange={e => setForm({ ...form, feeding_animal_count: e.target.value })}
                 placeholder={`Boş bırakırsan kayıtlı sayı (${form.animal_count || 0}) kullanılır`} />
        </Field>
        <Field label="Günlük Öğün Sayısı">
          <Input type="number" min="1" max="6" data-testid="ration-form-meals"
                 value={form.meals_per_day ?? ""}
                 placeholder="Örn: 2"
                 onChange={e => setForm({ ...form, meals_per_day: e.target.value })} />
        </Field>
        <div className="md:col-span-2">
          <Field label="Öğün Saatleri (virgülle ayırın, örn: 05:00, 12:00, 19:00)">
            <Input data-testid="ration-form-meal-times"
                   value={Array.isArray(form.meal_times) ? form.meal_times.join(", ") : (form.meal_times || "")}
                   onChange={e => setForm({ ...form, meal_times: e.target.value })}
                   placeholder="05:00, 12:00, 19:00" />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Notlar">
            <Textarea rows={2} value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} />
          </Field>
        </div>
      </div>

      {/* Grup Dağıtımı: hangi padoklara ne kadar yem dökülecek (Orana göre) */}
      <div className="mb-4 p-3 border border-[#E5E2DC] bg-[#FAF7F1] rounded" data-testid="ration-form-groups-block">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-sm font-semibold text-[#1A1D1A]">Grup Bazlı Dağıtım (Orana Göre Paylaşım)</div>
            <div className="text-[11px] text-[#5e675e]">
              <b>Toplam Porsiyon</b> yukarıdaki alandan alınır ve mikser bu miktara göre hazırlanır. Buradaki gruplara girdiğiniz hayvan sayıları YALNIZCA
              <b> oran (%)</b> için kullanılır. Örn: Toplam 100 · Grup A=60, B=40 → A'ya %60, B'ye %40 yem düşer.
            </div>
          </div>
          <Button type="button" data-testid="ration-form-add-group"
                  onClick={() => setForm({ ...form, feeding_groups: [...(form.feeding_groups || []), { _uid: rid(), name: "", animal_count: "" }] })}>
            <Plus size={14} className="inline mr-1" /> Grup Ekle
          </Button>
        </div>
        {(form.feeding_groups || []).length === 0 ? (
          <div className="text-xs text-[#8a938c] italic px-2 py-3 text-center">
            Grup eklenmedi. "Grup Ekle" ile padok/kısım eklerseniz sistem hazırlanan toplam yemi bu gruplara oranlarına göre böler.
          </div>
        ) : (
          <div className="space-y-2">
            {form.feeding_groups.map((g, gi) => {
              const cnt = Number(g.animal_count || 0);
              const ratio = groupsSum > 0 ? cnt / groupsSum : 0;
              const gDaily = totalKgAll * ratio;
              const gPerMeal = gDaily / mealsCount;
              return (
                <div key={g._uid} className="grid grid-cols-12 gap-2 items-center bg-white border border-[#E5E2DC] rounded px-2 py-2" data-testid={`ration-form-group-row-${gi}`}>
                  <Input className="col-span-4" data-testid={`ration-form-group-name-${gi}`}
                         placeholder="Grup / Padok adı (örn: Padok 1)"
                         value={g.name}
                         onChange={e => {
                           const gs = [...form.feeding_groups];
                           gs[gi] = { ...gs[gi], name: e.target.value };
                           setForm({ ...form, feeding_groups: gs });
                         }} />
                  <Input className="col-span-2" type="number" min="0" data-testid={`ration-form-group-count-${gi}`}
                         placeholder="Hayvan"
                         value={g.animal_count}
                         onChange={e => {
                           const gs = [...form.feeding_groups];
                           gs[gi] = { ...gs[gi], animal_count: e.target.value };
                           setForm({ ...form, feeding_groups: gs });
                         }} />
                  <div className="col-span-5 text-xs text-[#5e675e] tabnum flex items-center gap-3 flex-wrap">
                    {cnt > 0 && totalKgAll > 0 && groupsSum > 0 ? (
                      <>
                        <span className="text-[#8a938c]">%<b className="text-[#1A1D1A]">{fmt(ratio * 100, 1)}</b></span>
                        <span>Günlük: <b className="text-[#1A1D1A]">{fmt(gDaily, 1)} kg</b></span>
                        <span className="text-[#3E5B36]">Öğün: <b>{fmt(gPerMeal, 1)} kg</b></span>
                      </>
                    ) : <span className="text-[#8a938c] italic">Hayvan sayısı ve porsiyon girin</span>}
                  </div>
                  <button type="button" className="col-span-1 text-[#C45B45] hover:text-[#8B2E1D] p-1 justify-self-end"
                          data-testid={`ration-form-group-del-${gi}`}
                          onClick={() => setForm({ ...form, feeding_groups: form.feeding_groups.filter((_, i) => i !== gi) })}>
                    <Trash size={16} />
                  </button>
                </div>
              );
            })}
            {groupsSum > 0 && totalKgAll > 0 && (
              <div className="text-xs text-[#3E5B36] px-2 pt-1 border-t border-dashed border-[#C9D6BE]">
                Oran toplamı: <b>{groupsSum}</b> · Mikser hazırlığı: <b>{fmt(totalKgAll, 1)} kg/gün</b>
                {mealsCount > 1 && <> · Öğün başına: <b>{fmt(totalKgAll / mealsCount, 1)} kg</b></>}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add-item section */}
      <div className="grid md:grid-cols-2 gap-3 mb-4">
        <Card className="!p-3 !bg-[#F5F2EC]">
          <div className="text-xs font-semibold text-[#5e675e] mb-2">Stoktan ekle (stoktan otomatik düşülür)</div>
          <div className="max-h-32 overflow-y-auto space-y-1">
            {stockOptions.length === 0 && <div className="text-xs text-[#8a938c]">Stokta kalem yok</div>}
            {stockOptions.map(s => (
              <button key={s.id} data-testid={`ration-add-stock-${s.id}`} onClick={() => addItemFromStock(s)}
                      className="w-full text-left text-xs px-2 py-1.5 bg-white border border-[#E5E2DC] rounded hover:bg-[#E4EEE6] flex justify-between">
                <span><b>{s.name}</b> <span className="text-[#8a938c]">[{s.category}]</span></span>
                <span className="tabnum text-[#8a938c]">{fmt(s.quantity, 1)} {s.unit}{canSeeCosts ? ` @ ${fmt(s.unit_price, 2)}₺` : ""}</span>
              </button>
            ))}
          </div>
        </Card>
        <Card className="!p-3 !bg-[#F5F2EC]">
          <div className="text-xs font-semibold text-[#5e675e] mb-2">Katalogdan ekle (stokta olmasa da eklenebilir)</div>
          <div className="max-h-32 overflow-y-auto space-y-1">
            {catalogOptions.map(c => (
              <button key={c.id} data-testid={`ration-add-catalog-${c.id}`} onClick={() => addItemFromCatalog(c)}
                      className="w-full text-left text-xs px-2 py-1.5 bg-white border border-[#E5E2DC] rounded hover:bg-[#F0EAE0] flex justify-between">
                <span><b>{c.name}</b> <span className="text-[#8a938c]">[{c.category}]</span></span>
                <span className="tabnum text-[#8a938c]">%{c.protein_pct} P • {c.energy_mcal} Mcal</span>
              </button>
            ))}
          </div>
        </Card>
      </div>

      {/* Items table */}
      <div className="mb-4">
        <div className="text-sm font-semibold mb-2">Rasyon Kalemleri</div>
        {form.items.length === 0 ? (
          <div className="text-sm text-[#8a938c] text-center py-4 border border-dashed border-[#E5E2DC] rounded">
            Yukarıdan yem kalemi ekleyin
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Yem</th>
                <th className="text-right">kg/baş</th>
                {canSeeCosts && <th className="text-right">₺/kg</th>}
                <th className="text-right">Protein %</th>
                <th className="text-right">Mcal/kg</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {form.items.map((it, i) => (
                <tr key={it._uid} data-testid={`ration-item-${i}`}>
                  <td>
                    <div className="font-medium">{it.name}</div>
                    <div className="text-xs text-[#8a938c]">
                      {it.source === "stock" ? "🟢 Stok" : "📚 Katalog"}
                    </div>
                  </td>
                  <td>
                    <Input type="number" step="0.01" min="0" className="!py-1 text-right"
                           value={it.amount_per_head_kg}
                           onChange={e => updateItem(i, { amount_per_head_kg: e.target.value })} />
                  </td>
                  {canSeeCosts && (
                    <td>
                      <Input type="number" step="0.01" min="0" className="!py-1 text-right"
                             value={it.unit_price}
                             onChange={e => updateItem(i, { unit_price: e.target.value })} />
                    </td>
                  )}
                  <td>
                    <Input type="number" step="0.1" min="0" className="!py-1 text-right"
                           value={it.protein_pct}
                           onChange={e => updateItem(i, { protein_pct: e.target.value })} />
                  </td>
                  <td>
                    <Input type="number" step="0.1" min="0" className="!py-1 text-right"
                           value={it.energy_mcal}
                           onChange={e => updateItem(i, { energy_mcal: e.target.value })} />
                  </td>
                  <td className="text-right">
                    <button onClick={() => removeItem(i)} className="p-1.5 hover:bg-[#fbeee9] text-[#C45B45] rounded">
                      <Trash size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Live totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 p-4 bg-[#F5F2EC] rounded">
        <div><div className="text-xs text-[#8a938c]">Günlük/baş</div><div className="font-bold tabnum">{fmt(totalKgPerHead, 2)} kg</div></div>
        <div>
          <div className="text-xs text-[#8a938c]">Günlük Toplam İhtiyaç ({effectiveCount} baş)</div>
          <div className="font-bold tabnum" data-testid="ration-form-total-daily">{fmt(totalKgAll, 1)} kg</div>
          {form.feeding_animal_count !== "" && form.feeding_animal_count != null && Number(form.feeding_animal_count) !== Number(form.animal_count || 0) && (
            <div className="text-[10px] text-[#5e675e]">padokta {form.animal_count || 0} kayıtlı</div>
          )}
        </div>
        <div><div className="text-xs text-[#8a938c]">Protein/baş</div><div className="font-bold tabnum">{fmt(totalProteinG, 0)} g • %{totalKgPerHead > 0 ? fmt(totalProteinG / (totalKgPerHead * 10), 1) : 0}</div></div>
        <div><div className="text-xs text-[#8a938c]">Enerji/baş</div><div className="font-bold tabnum">{fmt(totalEnergy, 1)} Mcal</div></div>
      </div>

      {/* Öğün başına hazırlanacak miktar (canlı hesap) */}
      <div className="mb-4 p-4 bg-[#EEF3E8] border border-[#C9D6BE] rounded" data-testid="ration-form-per-meal">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-xs text-[#3E5B36] font-medium">Öğün Başına Hazırlanacak Miktar</div>
            <div className="text-[11px] text-[#5e675e] mt-0.5">
              {fmt(totalKgAll, 1)} kg ÷ <b>{mealsCount} öğün</b> · Baş başına {fmt(perMealPerHeadKg, 2)} kg
            </div>
          </div>
          <div className="text-2xl font-bold text-[#3E5B36] tabnum">{fmt(perMealKg, 1)} kg</div>
        </div>
        {canSeeCosts && perMealCost > 0 && (
          <div className="text-right text-xs text-[#5e675e] mt-1">
            Öğün başına maliyet: <b className="text-[#C45B45] tabnum">{fmt(perMealCost, 0)} ₺</b>
          </div>
        )}
      </div>
      {canSeeCosts && (
        <div className="text-right mb-4 text-sm">
          Günlük Toplam Maliyet: <b className="text-[#C45B45] tabnum text-lg">{fmt(totalCostAll, 0)} ₺</b>
        </div>
      )}

      <Button onClick={save} disabled={saving} data-testid="ration-form-save">
        {saving ? "Kaydediliyor..." : (initial.id ? "Güncelle" : "Kaydet")}
      </Button>
    </Card>
  );
}

// ---------------- EVENTS TAB ----------------
